import React, { useState, useMemo, useEffect } from "react";
import { Table, Typography } from "antd";
import { DigiUserFilters } from "../../types/admin/digiUserFilters";
import "../../style/dashboard/ListingRowColumn.css"

interface UserTableListProps {
  data: any[];
  filter: DigiUserFilters;
  currentUserRole?: string;
  menuName?: string;
  onRowClick?: (row: any) => void;
  defaultValue?: string; // optional default value for empty cells
}

const { Title } = Typography;
const UserTableList: React.FC<UserTableListProps> = ({
  data,
  filter,
  currentUserRole,
  menuName,
  onRowClick,
  defaultValue = "--", // default fallback if value is missing
}) => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, pageSizeOptions: ["10", "20", "50", "100"], showSizeChanger: true, });
  const [sorter, setSorter] = useState<{ field?: string; order?: string }>({});

  const handleTableChange = (pager: any, _: any, sorter: any) => {
    setPagination(pager);
    setSorter({ field: sorter.field, order: sorter.order });
  };


  // Reset pagination on filter change
  useEffect(() => {
    setPagination({ current: 1, pageSize: 10, pageSizeOptions: ["10", "20", "50", "100"], showSizeChanger: true, });
  }, [filter]);

  //const isMobile = window.innerWidth <= 576;

  const clickableCell = (record: any) => ({
    onClick: () => onRowClick?.(record),
    style: { cursor: "pointer" },
  });

  // Client-side sorting
  const sortedData = useMemo(() => {
    const field = sorter.field;
    const order = sorter.order;
    if (!field || !order) return data;

    const sorted = [...data].sort((a, b) => {
      const valA = a[field as keyof typeof a];
      const valB = b[field as keyof typeof b];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return valA - valB;
      }

      return String(valA).localeCompare(String(valB));
    });

    if (order === "descend") {
      sorted.reverse();
    }

    return sorted;
  }, [data, sorter]);

  const columns = [
    {
      title: () => <strong>#</strong>,
      key: "index",
      width: 40,
      align: "center" as "center",
      onCell: clickableCell,
      render: (_text: any, _record: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    }, {
      title: () => <strong>User Name</strong>,
      dataIndex: "userName",
      key: "userName",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>First Name</strong>,
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Middle Name</strong>,
      dataIndex: "middleName",
      key: "middleName",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Last Name</strong>,
      dataIndex: "lastName",
      key: "lastName",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Mobile Number</strong>,
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    },
    {
      title: () => <strong>Ancestor</strong>,
      dataIndex: "parentId",
      key: "parentId",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    },
    {
      title: () => <strong>Role</strong>,
      dataIndex: "role",
      key: "role",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Email</strong>,
      dataIndex: "email",
      key: "email",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Business Group</strong>,
      dataIndex: "businessGroup",
      key: "businessGroup",
      sorter: true,
      onCell: clickableCell,
      render: (value: string[] | null) => {
        if (!value || value.length === 0) return defaultValue;
        return value.join(", ");
      },
    }
  ];

  return (
    <div className="table-wrapper">
      {menuName && (
        <div className="table-title-header">
          <Title level={4} style={{ margin: 0 }}>
            {menuName}
          </Title>
        </div>
      )}
    
      <Table
        rowKey="id"
        columns={columns}
        dataSource={sortedData}
        pagination={{
          ...pagination,
          total: sortedData.length,
          position: ["bottomCenter"],
          showSizeChanger: true, 
          showQuickJumper: true,     // hide quick jumper on mobile
          
          //showTotal: (t) => `Total ${t} items`,
        }}
        sticky={{ offsetHeader: 0 }}      // ✅ Correct sticky header
        onChange={handleTableChange}
        rowClassName={(_, index) => (index % 2 === 0 ? "even-row" : "odd-row")}
        size="small"
        scroll={{
          x: "max-content",  // 🔥 horizontal scroll for mobile
          y: 412,             // existing vertical scroll
          scrollToFirstRowOnChange: true
        }}
      />
    </div>

  );
};

export default UserTableList;
